import { useState } from 'react';
import { Modal, Stack, Text, Group, Button } from '@mantine/core';
import { AlertTriangle } from 'react-feather';
import { notifications } from '@mantine/notifications';
import type { GroupWithMembers } from '../../../services/api/groups';
import { groupsApi } from '../../../services/api/groups';

interface ClearAllAssignmentsModalProps {
  opened: boolean;
  onClose: () => void;
  cohortId: string;
  groups: GroupWithMembers[];
  onCleared: () => void;
}

export function ClearAllAssignmentsModal({
  opened,
  onClose,
  cohortId,
  groups,
  onCleared,
}: ClearAllAssignmentsModalProps) {
  const [clearing, setClearing] = useState(false);

  const assignedGroups = groups.filter((g) => g.coach_id);

  const handleClearAll = async () => {
    setClearing(true);
    try {
      for (const group of assignedGroups) {
        await groupsApi.update(cohortId, group.id, { coachId: null });
      }
      notifications.show({
        title: 'Assignments Cleared',
        message: `Removed coach assignments from ${assignedGroups.length} group${assignedGroups.length !== 1 ? 's' : ''}`,
        color: 'green',
      });
      onCleared();
      onClose();
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to clear assignments',
        color: 'red',
      });
    } finally {
      setClearing(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Group gap="xs">
          <AlertTriangle size={18} color="#D64545" />
          <Text fw={600}>Clear All Coach Assignments?</Text>
        </Group>
      }
      size="sm"
    >
      <Stack gap="md">
        <Text size="sm" c="dimmed">
          This will remove all coach assignments from {assignedGroups.length} group
          {assignedGroups.length !== 1 ? 's' : ''} in this cohort. Coaches will need to be
          reassigned manually.
        </Text>
        <Group justify="flex-end" gap="sm">
          <Button variant="default" onClick={onClose} disabled={clearing}>
            Cancel
          </Button>
          <Button
            color="red"
            onClick={handleClearAll}
            loading={clearing}
            disabled={assignedGroups.length === 0}
          >
            Clear All
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
